import type {CodecFn} from './codecs';

type PropKind = 'string' | 'number' | 'boolean' | 'enum' | 'json';

type ManifestProp = {
    type: PropKind;
    default?: unknown;
    values?: readonly string[];
    description?: string;
    initialOnly?: boolean;
};

type ManifestPart = {
    selector: string;
    description: string;
};

type ManifestToken = {
    name: string;
    description: string;
};

type ManifestAcceptedEvent = {
    description: string;
    detail?: Record<string, string>;
};

type ComponentManifestMeta = {
    group?: string;
};

type ComponentManifest = {
    name: string;
    title: string;
    description: string;
    meta?: ComponentManifestMeta;
    props?: Record<string, ManifestProp>;
    propSchema?: Record<string, CodecFn<unknown>>;
    slots?: Record<string, string>;
    events?: Record<string, string>;
    acceptedEvents?: Record<string, ManifestAcceptedEvent>;
    parts?: Record<string, ManifestPart>;
    tokens?: readonly ManifestToken[];
};

type ManifestPropDetails = Pick<ManifestProp, 'description'> & {
    name?: string;
    initialOnly?: boolean;
};

type SchemaPropDetails<T extends Record<string, CodecFn<unknown>>> = {
    [K in keyof T]?: ManifestPropDetails;
};

type DefineManifestOptions<T extends ComponentManifest> = {
    manifest: T;
};

type PropsFromSchemaOptions<T extends Record<string, CodecFn<unknown>>> = {
    schema: T;
    details: SchemaPropDetails<T>;
};

const toSnake = (value: string): string => value.replace(/([A-Z])/g, '_$1').toLowerCase();

const defineManifest = <const T extends ComponentManifest>({manifest}: DefineManifestOptions<T>): Readonly<T> => Object.freeze(manifest);

const propsFromSchema = <const T extends Record<string, CodecFn<unknown>>>({
    schema,
    details,
}: PropsFromSchemaOptions<T>): Record<string, ManifestProp> => Object.fromEntries(
        Object.entries(schema).map(([schemaKey, codec]) => {
            const propDetails = details[schemaKey as keyof T] ?? {};
            const meta = codec.meta;
            const prop: ManifestProp = {
                type: meta.type,
                default: meta.default,
            };

            if (meta.values) prop.values = meta.values;
            if (propDetails.description) prop.description = propDetails.description;
            if (propDetails.initialOnly) prop.initialOnly = true;

            return [propDetails.name ?? toSnake(schemaKey), prop];
        }),
    );

export {defineManifest, propsFromSchema};
export type {
    ComponentManifest,
    DefineManifestOptions,
    ManifestPart,
    ManifestProp,
    ManifestPropDetails,
    ManifestAcceptedEvent,
    ComponentManifestMeta,
    ManifestToken,
    PropsFromSchemaOptions,
    PropKind,
    SchemaPropDetails,
};
